// File: CustomPaymentForm.js
import React, { useState } from 'react';
import "./style.scss";
import {
    CardNumberElement,
    CardExpiryElement,
    CardCvcElement,
    useStripe,
    useElements,
} from '@stripe/react-stripe-js';
import {relocateToPage} from "../../utils/common";
import {SKU_INFO} from "../../const/LocalStorage";
import {createSubscriptionRequest} from "../../utils/api";
import {getLoading} from "../../utils/loading";

const CARD_ELEMENT_OPTIONS = {
    style: {
        base: {
            fontSize: '16px',
            color: '#424770',
            fontFamily: 'Arial, sans-serif',
            '::placeholder': {
                color: '#aab7c4',
            },
        },
        invalid: {
            color: '#9e2146',
        },
    },
};

const CustomCardPaymentForm = ({ weightLossFormData, paymentType, price = {}, discount = 0, logEvent = () => {} }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const loadingRef = React.useRef(getLoading());

    const cardPaymentSuccess = (sessionId) => {
        localStorage.setItem(SKU_INFO, JSON.stringify({
            sku: price?.sku?.stripe,
            sku_id: price?.getUniqueId() || "",
            session_id: sessionId,
            payment_type: paymentType,
            discount: discount, // discount percentage
            price: price?.getPrice() || "",
            week: price?.week
        }));
        logEvent("paysuccess_click", {
            type: paymentType + "",
            sku: price?.sku?.stripe || "",
            percent: discount, // discount percentage
            string2: price?.getUniqueId() || "", // SKU ID
            string1: price?.getPrice() || "",
            string3: price?.week + ""
        }, "pay", true); // instantly = true

        relocateToPage(`/pay/verify?session_id=${sessionId}`);
    }

    const handleSubmit = async (event) => {
        event.preventDefault();
        
        // 防止重复提交
        if (loading) {
            return;
        }
        
        setLoading(true);
        setError(null);
        setSuccess(false);
        loadingRef.current?.show();

        if (!stripe || !elements) {
            setLoading(false);
            loadingRef.current?.hide();
            setError("Stripe.js not loaded properly.");
            return;
        }

        try {
            // 1. 创建订单并获取 clientSecret
            logEvent("paynow_click", { type: paymentType + "" }, "pay");
            
            const [sessionId, clientSecret] = await createSubscriptionRequest(
                price?.sku?.stripe,
                weightLossFormData.email,
                price.getDiscountValueForServer(),
                price.dynamicCycleSubscription,
                paymentType
            );
            
            if (!clientSecret) {
                logEvent("createfail_show", { type: paymentType + "" }, "pay");
                throw new Error("Failed to get client secret from backend response.");
            }
            
            logEvent("createsuccess_show", { type: paymentType + "" }, "pay");

            // 2. 验证表单输入
            const { error: submitError } = await elements.submit();
            if (submitError) {
                setError(submitError.message);
                setLoading(false);
                loadingRef.current?.hide();
                return;
            }

            // 3. 确认支付
            const cardNumberElement = elements.getElement(CardNumberElement);
            const cardExpiryElement = elements.getElement(CardExpiryElement);
            const cardCvcElement = elements.getElement(CardCvcElement);

            if (!cardNumberElement || !cardExpiryElement || !cardCvcElement) {
                setLoading(false);
                loadingRef.current?.hide();
                setError("Card input fields not found. Please ensure all elements are mounted correctly.");
                return;
            }

            const { error: confirmError, paymentIntent } = await stripe.confirmCardPayment(
                clientSecret,
                {
                    payment_method: {
                        card: cardNumberElement,
                    }
                },
            );

            if (confirmError) {
                setError(confirmError.message);
                setLoading(false);
                loadingRef.current?.hide();
            } else if (paymentIntent && (paymentIntent.status === 'succeeded' || paymentIntent.status === 'requires_action')) {
                loadingRef.current?.hide();
                cardPaymentSuccess(sessionId);
            } else {
                setError('Payment did not succeed. Status: ' + (paymentIntent?.status || 'Unknown'));
                setLoading(false);
                loadingRef.current?.hide();
            }
        } catch (err) {
            console.error("Error during payment:", err);
            setError(err.message || "An error occurred during payment processing.");
            setLoading(false);
            loadingRef.current?.hide();
        }
    };

    return (
        <form onSubmit={handleSubmit} className={'stripe-form'}>
            <img className="bank-icon" alt="Icon" src="/static/image/questionnaire/weight-loss/paymentFastMethods/bank-icons.png" />

            <div className={'stripe-input'}>
                {/* 信用卡号 */}
                <div className={"card-number"}>
                    <CardNumberElement id="card-number" options={CARD_ELEMENT_OPTIONS} />
                </div>
                {/* 有效期和CVC */}
                <div className={'cards'}>
                    <div className={'MM-YY'}>
                        <CardExpiryElement id="card-expiry" options={CARD_ELEMENT_OPTIONS} />
                    </div>
                    <div className={'CVC'}>
                        <CardCvcElement id="card-cvc" options={CARD_ELEMENT_OPTIONS} />
                    </div>
                </div>
            </div>

            <div 
                className={'bottom-btn'}
                onClick={handleSubmit}
                style={{ 
                    cursor: (!stripe || !elements || loading) ? 'not-allowed' : 'pointer',
                    opacity: (!stripe || !elements || loading) ? 0.6 : 1
                }}
            >
                <img className="bottom-icon" alt="Frame" src="/static/image/questionnaire/weight-loss/paymentFastMethods/frame-1.png" />
                <button
                    disabled={!stripe || !elements || loading}
                    className={'continue-button'}
                    style={{ pointerEvents: 'none' }}
                >
                    {loading ? 'Processing...' : 'Pay Now'}
                </button>
            </div>
            {error && <div style={{ color: 'red', marginTop: '15px', textAlign: 'center' }}>{error}</div>}
            {success && <div style={{ color: 'green', marginTop: '15px', textAlign: 'center' }}>Payment Successful!</div>}
        </form>
    );
};

export default CustomCardPaymentForm;